import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Chip,
  IconButton,
  Checkbox,
  FormControlLabel,
  Stack,
  Alert,

} from '@mui/material';
import { Wallpaper, Delete, Add, CloudUpload } from '@mui/icons-material';
import axios from 'axios';
import RichTextEditor from './RichTextEditor';
import WallpaperPositionPicker from './WallpaperPositionPicker';
import EventThemePicker from './EventThemePicker';

interface FormData {
  title: string;
  description: string;
  date: string;
  location: string;
  needed_items: string[];
  rsvp_cutoff_date: string;
  max_guests_per_rsvp: number;
  email_notifications_enabled: boolean;
  email_recipients: string;
  event_conclusion_message: string;
  event_conclusion_email_enabled: boolean;
}

const EventForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    date: '',
    location: '',
    needed_items: [],
    rsvp_cutoff_date: '',
    max_guests_per_rsvp: 0,
    email_notifications_enabled: false,
    email_recipients: '',
    event_conclusion_message: '',
    event_conclusion_email_enabled: false,
  });
  const [wallpaper, setWallpaper] = useState<File | null>(null);
  const [wallpaperPosition, setWallpaperPosition] = useState('50% 50%');
  const [wallpaperZoom, setWallpaperZoom] = useState(1.0);
  const [themeId, setThemeId] = useState<string | null>('classic');
  const [currentItem, setCurrentItem] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pending = React.useRef(false);

  const wallpaperPreviewUrl = useMemo(() => {
    if (!wallpaper) return null;
    return URL.createObjectURL(wallpaper);
  }, [wallpaper]);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleWallpaperClick = () => {
    fileInputRef.current?.click();
  };

  const handleWallpaperChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setWallpaper(e.target.files[0]);
      setWallpaperPosition('50% 50%');
      setWallpaperZoom(1.0);
    }
  };

  const handleAddItem = () => {
    if (currentItem.trim()) {
      setFormData((prev) => ({
        ...prev,
        needed_items: [...prev.needed_items, currentItem.trim()],
      }));
      setCurrentItem('');
    }
  };

  const handleRemoveItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      needed_items: prev.needed_items.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending.current) return;
    pending.current = true;
    setIsSubmitting(true);
    setError(null);
    try {
      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'needed_items') {
          submitData.append(key, JSON.stringify(value));
        } else {
          submitData.append(key, String(value));
        }
      });
      if (wallpaper) {
        submitData.append('wallpaper', wallpaper);
      }
      submitData.append('wallpaper_position', wallpaperPosition);
      submitData.append('wallpaper_zoom', wallpaperZoom.toString());
      submitData.append('theme_id', themeId || 'classic');
      const response = await axios.post('/api/events', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate(response.data?.slug ? `/admin/events/${encodeURIComponent(response.data.slug)}` : '/', {
        state: { eventCreated: true },
      });
    } catch (error) {
      if (axios.isAxiosError(error) && !error.response) {
        const responseUrl = (error.request as XMLHttpRequest | undefined)?.responseURL || '';
        if (responseUrl.includes('/application/o/authorize')) {
          // Force a full-page auth check through Caddy/Authentik for SPA navigations.
          window.location.reload();
          return;
        }
      }
      setError('Failed to create event. Please try again.');
      console.error('Error creating event:', error);
    } finally {
      pending.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Create New Event
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Fill in the details below to set up your event.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        {/* Section 1: Basic Details */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2.5 }}>Basic Details</Typography>
          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
            <RichTextEditor
              value={formData.description}
              onChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
              label="Description"
              placeholder="Enter event description with formatting..."
            />
            <TextField
              fullWidth
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              label="Date and Time"
              name="date"
              type="datetime-local"
              value={formData.date}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="RSVP Cut-off Date"
              name="rsvp_cutoff_date"
              type="datetime-local"
              value={formData.rsvp_cutoff_date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Maximum Additional Guests Per RSVP"
              name="max_guests_per_rsvp"
              type="number"
              value={formData.max_guests_per_rsvp}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                setFormData((prev) => ({
                  ...prev,
                  max_guests_per_rsvp: isNaN(value) ? 0 : value,
                }));
              }}
              helperText="Set to 0 for no additional guests, -1 for unlimited"
              inputProps={{ min: -1 }}
            />
          </Stack>
        </Paper>

        {/* Section 2: Needed Items */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2.5 }}>Needed Items</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              label="Add Item"
              value={currentItem}
              onChange={(e) => setCurrentItem(e.target.value)}
              size="small"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddItem(); } }}
            />
            <Button
              variant="contained"
              onClick={handleAddItem}
              disabled={!currentItem.trim()}
              startIcon={<Add />}
            >
              Add
            </Button>
          </Box>
          {formData.needed_items.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.needed_items.map((item, index) => (
                <Chip
                  key={index}
                  label={item}
                  onDelete={() => handleRemoveItem(index)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Paper>

        {/* Section 3: Customization */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2.5 }}>Customization</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Event Wallpaper
          </Typography>
          <Box
            onClick={handleWallpaperClick}
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 0.2s ease, background-color 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            {wallpaper ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                <Wallpaper color="primary" />
                <Typography variant="body2">{wallpaper.name}</Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => { e.stopPropagation(); setWallpaper(null); setWallpaperPosition('50% 50%'); }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box>
                <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.5, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Click to upload a wallpaper image
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  JPG, PNG, or GIF
                </Typography>
              </Box>
            )}
          </Box>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleWallpaperChange}
            accept="image/jpeg,image/png,image/gif"
            style={{ display: 'none' }}
          />
          {wallpaperPreviewUrl && (
            <Box sx={{ mt: 2.5 }}>
              <WallpaperPositionPicker
                imageUrl={wallpaperPreviewUrl}
                position={wallpaperPosition}
                onChange={setWallpaperPosition}
                zoom={wallpaperZoom}
                onZoomChange={setWallpaperZoom}
              />
            </Box>
          )}
        </Paper>

        {/* Section 4: Style */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <EventThemePicker value={themeId} onChange={setThemeId} title={formData.title} description={formData.description} date={formData.date} />
        </Paper>

        {/* Section 5: Notifications */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2.5 }}>Notifications</Typography>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.email_notifications_enabled}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email_notifications_enabled: e.target.checked }))
                  }
                />
              }
              label="Enable email notifications"
            />
            {formData.email_notifications_enabled && (
              <TextField
                fullWidth
                label="Email Recipients (comma separated)"
                name="email_recipients"
                value={formData.email_recipients}
                onChange={handleChange}
                placeholder="email1@example.com, email2@example.com"
                helperText="Enter email addresses separated by commas"
              />
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.event_conclusion_email_enabled}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, event_conclusion_email_enabled: e.target.checked }))
                  }
                />
              }
              label="Enable event conclusion email"
            />
            {formData.event_conclusion_email_enabled && (
              <RichTextEditor
                value={formData.event_conclusion_message}
                onChange={(value) => setFormData((prev) => ({ ...prev, event_conclusion_message: value }))}
                label="Event conclusion message"
                placeholder="Enter the message to send after the event..."
                helperText="This message will be sent to attendees who opted for email notifications the day after the event."
              />
            )}
          </Stack>
        </Paper>

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={isSubmitting}
          sx={{ py: 1.5 }}
        >
          {isSubmitting ? 'Creating…' : 'Create Event'}
        </Button>
      </Box>
    </Box>
  );
};

export default EventForm;
